import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spin } from 'antd';
import { cloneDeep } from 'es-toolkit';

import { LOSS_AND_DAMAGE_CLAIMS_FILTERS_INITIAL_DATA } from '../../../../../contants/ClaimsEnums';
import { SEARCH_VIEW_TYPES } from '../../../../../contants/common';
import {
  setLADClaimEditDrawer,
  setLossAndDamageFilters,
  setSelectedClaimKey,
  setSelectedLADClaims,
} from '../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { setPageBlocker } from '../../../../../redux/common/CommonActions';
import EmptyRecords from '../../../../components/Common/EmptyRecords';
import Drawer from '../../../../components/Modals/Drawer';
import ScrollPagination from '../../../../components/Paginations/ScrollPagination';
import Paper from '../../../../components/Paper/Paper';
import LADDrawerHeader from '../Edit/LADDrawerHeader';
import LADEditTabs from '../Edit/LADEditTabs';
import { useGetClaimDetails, useGetClaimsList } from '../hooks';
import LDTableView from './LDTableView';
import LossAndDamageListCard from './LossAndDamageListCard';

const LossAndDamageList = () => {
  const dispatch = useDispatch();
  const selectedCompany = useSelector((state) => state.auth.selectedCompany);

  const { filters, selectedClaims, editDrawer, claimViewMode, filterType, selectedClaimKey } = useSelector(
    (state) => state.lossAndDamage,
  );

  const {
    data: claimsListData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useGetClaimsList({
    filters,
    filterType,
  });

  const hasRows = (claimsListData?.flatClaims?.length ?? 0) > 0;
  const showInitialSkeleton = isLoading && !hasRows;
  const showEmptyState = !isLoading && !isFetching && !hasRows;

  const claimsList = useMemo(() => {
    return {
      data: claimsListData?.flatClaims ?? [],
      footerData: claimsListData?.footerData ?? null,
      isFirstTime: showInitialSkeleton,
    };
  }, [claimsListData, showInitialSkeleton]);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (selectedCompany?.companyId) {
      dispatch(
        setLossAndDamageFilters({
          ...LOSS_AND_DAMAGE_CLAIMS_FILTERS_INITIAL_DATA,
          pageNo: 1,
        }),
      );
    }
  }, [selectedCompany?.companyId, dispatch]);

  const onSelect = (id, remove) => {
    const selectedClaimLocal = cloneDeep(selectedClaims);
    if (!remove) {
      selectedClaimLocal.push(id);
    } else {
      const index = selectedClaimLocal.indexOf(id);
      selectedClaimLocal.splice(index, 1);
    }
    dispatch(setSelectedLADClaims(selectedClaimLocal));
  };

  const handlePageBlockerAfterClosingEditDrawer = () => {
    dispatch(setLADClaimEditDrawer(false));
    dispatch(setPageBlocker(false));
  };

  const handleClaimClick = (claimKey) => {
    dispatch(setSelectedClaimKey(claimKey));
    dispatch(setLADClaimEditDrawer(true));
  };

  const { data: claimDetails, isLoading: isClaimDetailsLoading } = useGetClaimDetails(
    editDrawer ? selectedClaimKey : undefined,
  );

  const handleAfterEditDrawerClosed = (val) => {
    if (!val) {
      dispatch(setPageBlocker(false));
    }
  };

  const isCardView = useMemo(() => claimViewMode === SEARCH_VIEW_TYPES.CARD, [claimViewMode]);

  return (
    <>
      <div className="h-100 pt-15 d-flex flex-direction-column">
        {isCardView ? (
          claimsList.isFirstTime ? (
            <Paper className="h-100 w-100 mb-15" />
          ) : (
            <ScrollPagination
              className={`${claimsList?.data?.length > 0 ? 'mb-15' : 'pb-15'} h-100`}
              loadMore={handleLoadMore}
              hasMore={hasNextPage ?? false}
              isLoading={isFetchingNextPage}
              scrollOffset={50}
            >
              {claimsList?.data && claimsList?.data.length > 0
                ? claimsList?.data.map((row) => (
                    <div className="mb-15" key={row.claimKey}>
                      <LossAndDamageListCard
                        data={row}
                        handleClaimClick={handleClaimClick}
                        onSelect={onSelect}
                        isSelected={selectedClaims.includes(row.claimKey)}
                      />
                    </div>
                  ))
                : showEmptyState && <EmptyRecords />}
            </ScrollPagination>
          )
        ) : (
          <LDTableView
            handleLoadMore={handleLoadMore}
            isLoading={isFetchingNextPage}
            hasMore={hasNextPage ?? false}
            claimsList={claimsList?.data ? claimsList.data : []}
            handleShipmentClick={handleClaimClick}
            onSelect={onSelect}
            selectedClaims={selectedClaims}
            isFirstTime={claimsList.isFirstTime}
          />
        )}
      </div>
      <Drawer
        open={editDrawer}
        onClose={handlePageBlockerAfterClosingEditDrawer}
        headerContents={<LADDrawerHeader claimData={claimDetails} />}
        width="90%"
        afterOpenChange={handleAfterEditDrawerClosed}
        destroyOnHidden
      >
        <Spin
          size="large"
          spinning={isClaimDetailsLoading}
          wrapperClassName="h-100 overflow-hidden shipment-drawer-root-spinner"
        >
          <LADEditTabs />
        </Spin>
      </Drawer>
    </>
  );
};

export default LossAndDamageList;
